import type { JSX } from 'solid-js';
import { Show } from 'solid-js';

// Hand-authored inline-SVG icon set. No icon font, no FontAwesome, no `solid-fa`,
// no JS — each icon is a Solid component that renders a stroke-based <svg> sized
// in `em` and painted with `currentColor`, so it inherits font-size and color
// from its container. Decorative by default (`aria-hidden="true"`); pass `label`
// to expose it to assistive tech as an image with an accessible name.
//
// Geometry: 24x24 viewBox, `fill="none"`, `stroke-width="2"`, round caps/joins.
// Keep paths simple and self-contained; this is the canonical icon source for
// the marketing site (loops, stats, trust band, CTAs).

export type IconSvgProps = {
  // CSS length for both width and height. Defaults to `1em` (tracks font-size).
  readonly size?: string;
  // Accessible name. When set, the icon becomes `role="img"` with a <title>;
  // otherwise it stays `aria-hidden` (decorative).
  readonly label?: string;
  readonly class?: string;
};

type SvgProps = IconSvgProps & { readonly children: JSX.Element };

function Svg(props: SvgProps): JSX.Element {
  const size = (): string => props.size ?? '1em';
  return (
    <svg
      class={props.class}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size()}
      height={size()}
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      role={props.label ? 'img' : undefined}
      aria-label={props.label}
      aria-hidden={props.label ? undefined : 'true'}
    >
      <Show when={props.label}>
        <title>{props.label}</title>
      </Show>
      {props.children}
    </svg>
  );
}

// Cyclical arrows — the maintainer "loop" (issue / PR / release cycles).
export function IconLoop(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v1a4 4 0 0 1-4 4H3" />
    </Svg>
  );
}

// Power plug — the "integrations / connect" glyph (click-to-connect connectors).
export function IconPlug(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 22v-5" />
      <path d="M9 8V2" />
      <path d="M15 8V2" />
      <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" />
    </Svg>
  );
}

// GitHub-style issue glyph: a ring with a centered dot.
export function IconIssue(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="2.25" fill="currentColor" stroke="none" />
    </Svg>
  );
}

// Pull request: source/target nodes with a branch arm.
export function IconPR(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M6 9v12" />
      <path d="M13 6h3a2 2 0 0 1 2 2v7" />
    </Svg>
  );
}

// Release: a version tag with its hole.
export function IconRelease(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 12l6-7h11v14H9z" />
      <circle cx="8.5" cy="12" r="1.5" />
    </Svg>
  );
}

// Shield with a check — audit / verified.
export function IconShield(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </Svg>
  );
}

// Key — BYOK (bring your own key).
export function IconKey(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="M21 2l-9.6 9.6" />
      <path d="M15.5 7.5l3 3" />
    </Svg>
  );
}

// Robot head — the bot (always discloses).
export function IconBot(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 8V5" />
      <circle cx="12" cy="4" r="1" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M9 13v2" />
      <path d="M15 13v2" />
    </Svg>
  );
}

// Right-pointing arrow — CTAs, "see more".
export function IconArrowRight(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </Svg>
  );
}

// Checkmark — feature dots, success.
export function IconCheck(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M20 6L9 17l-5-5" />
    </Svg>
  );
}

// Code brackets — a repository.
export function IconRepo(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 8l-4 4 4 4" />
      <path d="M15 8l4 4-4 4" />
    </Svg>
  );
}

// Fan-out of connected nodes — webhook deliveries.
export function IconWebhook(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4" />
      <path d="M15.4 6.5l-6.8 4" />
    </Svg>
  );
}

// Envelope — email routes.
export function IconMail(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </Svg>
  );
}

// Card with a stripe — billing / usage.
export function IconCard(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
    </Svg>
  );
}

// Ruled page — weekly digests.
export function IconDigest(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6" />
      <path d="M9 12h6" />
      <path d="M9 16h4" />
    </Svg>
  );
}

// Sun — light-theme toggle.
export function IconSun(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </Svg>
  );
}

// Crescent — dark-theme toggle.
export function IconMoon(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </Svg>
  );
}

// Two bars — pause motion (the nav's site-wide toggle, at rest).
export function IconPause(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 5v14M15 5v14" />
    </Svg>
  );
}

// Triangle — play; the nav's motion toggle while motion is held.
export function IconPlay(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M7 5l12 7-12 7z" />
    </Svg>
  );
}

// Overlapping sheets — copy to clipboard.
export function IconCopy(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Svg>
  );
}

// Trash can — delete / remove.
export function IconTrash(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
    </Svg>
  );
}

// Padlock (closed) — a required / non-editable route.
export function IconLock(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </Svg>
  );
}

// Magnifier — search / filter input.
export function IconSearch(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </Svg>
  );
}

// Chevron pointing down — expand / disclose.
export function IconChevronDown(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6 9l6 6 6-6" />
    </Svg>
  );
}

// Arrow into a tray — download / export (members CSV).
export function IconDownload(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </Svg>
  );
}

// Triangle with a bang — warning / attention.
export function IconAlert(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3L2 20h20L12 3z" />
      <path d="M12 10v4" />
      <path d="M12 17.5v.5" />
    </Svg>
  );
}

// Ring with an 'i' — informational note.
export function IconInfo(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8v.5" />
    </Svg>
  );
}

// Plus — add / create.
export function IconPlus(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Svg>
  );
}

// Bell — notifications.
export function IconBell(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </Svg>
  );
}

// Ringing bell — device push (distinct from IconBell, the notifications inbox).
export function IconBellRing(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      <path d="M2 8c0-2.2.7-4.3 2-6" />
      <path d="M22 8c0-2.2-.7-4.3-2-6" />
    </Svg>
  );
}

// At-sign — email routes.
export function IconAt(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.9 7.9" />
    </Svg>
  );
}

// X / close — negative state in comparison tables, dismiss actions.
export function IconX(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Svg>
  );
}

// Menu / hamburger — opens the off-canvas sidebar on narrow screens.
export function IconMenu(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </Svg>
  );
}

// GitHub octocat mark — a fill glyph, so it opts out of the stroke defaults.
export function IconGitHub(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55v-2.15c-3.2.7-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.04-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.02 1.76 2.69 1.25 3.35.96.1-.75.4-1.25.73-1.54-2.55-.29-5.23-1.28-5.23-5.68 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.1 11.1 0 0 1 5.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.12 3.05.74.81 1.18 1.83 1.18 3.09 0 4.41-2.69 5.38-5.25 5.66.41.36.78 1.05.78 2.12v3.15c0 .3.2.67.8.55A11.55 11.55 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"
      />
    </Svg>
  );
}

// ─── BYOK provider marks ─────────────────────────────────────────────────────
// Brand glyphs for the inference providers, shown beside the provider name on
// the Keys pages (#801). Fill glyphs like IconGitHub above, painted in
// `currentColor` so the caller decides monochrome vs brand tint — we do NOT
// ship the vendors' brand colours baked in.
//
// ponytail: only the marks that are pure geometry live here. A provider with no
// entry falls back to a monogram tile (see `ProviderMark` in the dashboard), so
// adding one later is a one-line map entry — no caller changes.

// Anthropic — the wordmark's "A" glyph.
export function IconAnthropic(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z"
      />
    </Svg>
  );
}

// Gemini — the four-point spark (four concave quadrant arcs).
export function IconGemini(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.305 14.305 0 0 0 12 12 14.305 14.305 0 0 0-12 12"
      />
    </Svg>
  );
}

// OpenAI — the interlocking knot.
export function IconOpenAI(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
      />
    </Svg>
  );
}

// OpenRouter — the routing chevron.
export function IconOpenRouter(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M16.778 1.844v1.919q-.569-.026-1.138-.032-.708-.008-1.415.037c-1.93.126-4.023.728-6.149 2.237-2.911 2.066-2.731 1.95-4.14 2.75-.396.223-1.342.574-2.185.798-.841.225-1.753.333-1.751.333v4.229s.768.108 1.61.333c.842.224 1.789.575 2.185.799 1.41.798 1.228.683 4.14 2.75 2.126 1.509 4.22 2.11 6.148 2.236.88.058 1.716.041 2.555.005v1.918l7.222-4.168-7.222-4.17v2.176c-.86.038-1.611.065-2.278.021-1.364-.09-2.417-.357-3.979-1.465-2.244-1.593-2.866-2.027-3.68-2.508.889-.518 1.449-.906 3.822-2.59 1.56-1.109 2.614-1.377 3.978-1.466.667-.044 1.418-.017 2.278.02v2.176L24 6.014Z"
      />
    </Svg>
  );
}

// Kimi (Moonshot) — the wordmark glyph.
export function IconKimi(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M21.765.351C22.998.351 24 1.353 24 2.586S22.998 4.82 21.765 4.82h-1.974c-.15 0-.26-.12-.26-.26V2.586A2.237 2.237 0 0 1 21.765.35M9.41 13.388l8.447-8.377c.16-.16.07-.471-.14-.471h-4.55s-.1.02-.14.06l-9.099 9.029c-.14.14-.35.02-.35-.21V4.81c0-.15-.1-.27-.221-.27H.22c-.12 0-.22.12-.22.27v18.57c0 .15.1.27.22.27h3.137c.12 0 .22-.12.22-.27v-3.79c0-.08.03-.16.08-.21l2.826-2.796c.07-.07.16-.08.241-.03l7.546 5.551a8.9 8.9 0 0 0 4.018 1.493c.12.01.23-.11.23-.27V19.76c0-.14-.08-.25-.19-.26a5.8 5.8 0 0 1-2.355-.942l-6.533-4.73c-.14-.09-.15-.32-.03-.441"
      />
    </Svg>
  );
}

// MiniMax — the stepped bar mark.
export function IconMiniMax(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        stroke="none"
        d="M11.43 3.92a.86.86 0 1 0-1.718 0v14.236a1.999 1.999 0 0 1-3.997 0V9.022a.86.86 0 1 0-1.718 0v3.87a1.999 1.999 0 0 1-3.997 0V11.49a.57.57 0 0 1 1.139 0v1.404a.86.86 0 0 0 1.719 0V9.022a1.999 1.999 0 0 1 3.997 0v9.134a.86.86 0 0 0 1.719 0V3.92a1.998 1.998 0 1 1 3.996 0v11.788a.57.57 0 1 1-1.139 0zm10.572 3.105a2 2 0 0 0-1.999 1.997v7.63a.86.86 0 0 1-1.718 0V3.923a1.999 1.999 0 0 0-3.997 0v16.16a.86.86 0 0 1-1.719 0V18.08a.57.57 0 1 0-1.138 0v2a1.998 1.998 0 0 0 3.996 0V3.92a.86.86 0 0 1 1.719 0v12.73a1.999 1.999 0 0 0 3.996 0V9.023a.86.86 0 1 1 1.72 0v6.686a.57.57 0 0 0 1.138 0V9.022a2 2 0 0 0-1.998-1.997"
      />
    </Svg>
  );
}
// Office building — an organization.
export function IconOrg(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 21v-4h6v4" />
      <path d="M8 7h2M14 7h2M8 11h2M14 11h2" />
    </Svg>
  );
}

// Rocket — scale / launch: one developer multiplies into a fleet; shipping releases.
export function IconRocket(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </Svg>
  );
}

// Clock — around-the-clock / 24-7 oncall.
export function IconClock(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </Svg>
  );
}

// Branch — a code branch (git-branch).
export function IconGitBranch(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6 3v12" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </Svg>
  );
}

// Chip — compute / the thin orchestrator (cpu).
export function IconCpu(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2" />
      <path d="M2 9h2M2 15h2M20 9h2M20 15h2" />
    </Svg>
  );
}

// Stacked sheets — layers / a stack of repos or environments.
export function IconLayers(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3l9 5-9 5-9-5z" />
      <path d="M3 12l9 5 9-5" />
      <path d="M3 16.5l9 5 9-5" />
    </Svg>
  );
}

// Lightning bolt — speed / instant action (zap).
export function IconBolt(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
    </Svg>
  );
}

// Stacked server racks — infrastructure / hosting.
export function IconServer(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="18" height="7" rx="2" />
      <rect x="3" y="14" width="18" height="7" rx="2" />
      <path d="M7 6.5h.01" />
      <path d="M7 17.5h.01" />
    </Svg>
  );
}

// Pulse line — activity / live telemetry (admin radar).
export function IconActivity(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </Svg>
  );
}

// Ring with an exclamation — inline error / alert circle.
export function IconAlertCircle(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </Svg>
  );
}

// Ring with a check — toast success.
export function IconCircleCheck(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  );
}

// Octagon with an X — toast error (stop-sign severity).
export function IconOctagonX(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86z" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </Svg>
  );
}

// Tiled panels — dashboards.
export function IconLayoutDashboard(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </Svg>
  );
}

// Horizontal ellipsis — overflow affordance.
export function IconMoreHorizontal(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 12h.01" />
      <path d="M12 12h.01" />
      <path d="M19 12h.01" />
    </Svg>
  );
}

// Door + arrow — sign out.
export function IconLogOut(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </Svg>
  );
}

// House — the org's home (the rail's Home row).
export function IconHome(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
      <path d="M10 21v-6h4v6" />
    </Svg>
  );
}

// Prompt chevron + cursor line — the terminal.
export function IconTerminal(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="m4 17 6-6-6-6" />
      <path d="M12 19h8" />
    </Svg>
  );
}

// Two heads — members, users.
export function IconUsers(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  );
}

// Speech bubble — prompts.
export function IconMessage(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </Svg>
  );
}

// Curly braces — variables (prompt vars).
export function IconBraces(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5a2 2 0 0 0 2 2h1" />
      <path d="M16 21h1a2 2 0 0 0 2-2v-5a2 2 0 0 1 2-2 2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" />
    </Svg>
  );
}

// Heart — donations.
export function IconHeart(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7z" />
    </Svg>
  );
}

// A panel with its left column ruled off — the sidebar's collapse toggle.
export function IconPanelLeft(props: IconSvgProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
    </Svg>
  );
}
